import { DataProvider } from '@repo/ui/dataProvider'
import { createBrowserRouter } from 'react-router-dom'

import { LayoutManager } from '@/components/layouts/LayoutManager'
import { AppDispatch } from '@/main'
import { RouteNames } from '@/routes'
import { AdminRoute } from '@/routes/AdminRoute.tsx'
import { BeneficiaryChatRoute } from '@/routes/beneficiary/BeneficiaryChatRoute'
import { BeneficiaryTasksRoute } from '@/routes/beneficiary/BeneficiaryTasksRoute'
import { BeneficiaryRoute } from '@/routes/BeneficiaryRoute.tsx'
import { CheckOnboarding } from '@/routes/CheckOnboarding.tsx'
import { ErrorRoute } from '@/routes/ErrorRoute.tsx'
import { LoadingRoute } from '@/routes/LoadingRoute'
import { LobbyRoute } from '@/routes/LobbyRoute'
import { LoginRoute } from '@/routes/LoginRoute.tsx'
import { OnboardingRoute } from '@/routes/OnboardingRoute.tsx'
import { OrganizationProtectedRoute } from '@/routes/OrganizationProtectedRoute'
import { OrganizationRoute } from '@/routes/OrganizationRoute.tsx'
import { ResetPasswordRoute } from '@/routes/ResetPasswordRoute.tsx'
import { Root } from '@/routes/Root.tsx'
import { SignUpRoute } from '@/routes/SignUpRoute.tsx'
import { DemoRoute } from '@/routes/sudo/DemoRoute'
import { SudoRoute } from '@/routes/sudo/SudoRoute'
import { UserProtectedRoute } from '@/routes/UserProtectedRoute'
import { RootState } from '@/store'
import { storeActions } from '@/store/actions'
import { checkAuth } from '@/store/auth/thunks'

export type AvailableParams = {
  beneficiaryId?: string
  chatSessionId?: string
  organizationId?: string
}

const adminPath = RouteNames.Admin
const beneficiariesPath = RouteNames.Beneficiaries + '/:beneficiaryId'
const beneficiaryChatPath = RouteNames.BeneficiaryChat + '/:chatSessionId'
const beneficiaryTasksPath = RouteNames.BeneficiaryTasks
const demoPath = RouteNames.Root + RouteNames.Demo
const onboardingPath = RouteNames.Onboarding
const organizationsPath = RouteNames.Organizations + '/:organizationId'
const resetPasswordPath = RouteNames.Root + RouteNames.ResetPassword + '/:email/:token'
const rootLoginPath = RouteNames.Root + RouteNames.Login
const signUpPath = RouteNames.Root + RouteNames.SignUp
const sudoPath = RouteNames.Root + RouteNames.Sudo

export const router = (dispatch: AppDispatch) =>
  createBrowserRouter([
    {
      path: RouteNames.Root,
      element: <Root />,
      errorElement: <ErrorRoute />,
      children: [
        {
          path: rootLoginPath,
          element: <LoginRoute />,
        },
        {
          path: signUpPath,
          element: <SignUpRoute />,
        },
        {
          path: resetPasswordPath,
          element: <ResetPasswordRoute />,
        },

        //
        // User Protected Routes
        //
        {
          loader: () => {
            void dispatch(checkAuth())
            dispatch(storeActions.cleanUpOrganization())
            return null
          },
          element: <UserProtectedRoute />,
          children: [
            {
              path: '/',
              element: (
                <DataProvider
                  loadingComponent={<LoadingRoute />}
                  errorComponent={<ErrorRoute message="Unable to load organizations." />}
                  selectors={[(state: RootState) => state.user.organizations]}
                  render={(organizations) => <LobbyRoute organizations={organizations} />}
                />
              ),
            },
            //
            // Sudo related routes
            //
            {
              path: sudoPath,
              element: <SudoRoute />,
            },
            {
              path: demoPath,
              element: <DemoRoute />,
            },
          ],
        },

        //
        // Organization Protected routes
        //
        {
          loader: () => {
            void dispatch(checkAuth())
            return null
          },
          element: <OrganizationProtectedRoute />,
          children: [
            //
            // Organization related routes
            //
            {
              path: organizationsPath,
              element: (
                <CheckOnboarding>
                  <LayoutManager>
                    <OrganizationRoute />
                  </LayoutManager>
                </CheckOnboarding>
              ),
              children: [
                {
                  path: beneficiariesPath,
                  element: <BeneficiaryRoute />,
                  children: [
                    {
                      path: beneficiaryChatPath,
                      element: <BeneficiaryChatRoute />,
                    },
                    {
                      path: beneficiaryTasksPath,
                      element: <BeneficiaryTasksRoute />,
                    },
                  ],
                },
                {
                  path: adminPath,
                  element: <AdminRoute />,
                },
                {
                  path: onboardingPath,
                  element: <OnboardingRoute />,
                },
              ],
            },
          ],
        },

        {
          path: '*',
          element: <ErrorRoute message="404 page not found." />,
        },
      ],
    },
  ])
